export interface FlowExplainerStep {
  id: string;
  title: string;
  description: string;
}

export interface OntologyWorkbenchStep {
  id: number;
  label: string;
}

export type CqStatus = "Approved" | "Needs Review" | "Draft" | "Rejected";

// Matches the backend's OntologyCompetencyQuestionItemResponse (GET/POST/PUT competency-questions).
export interface CompetencyQuestionApiItem {
  cqId: string;
  initiativeId: string;
  businessQuestionId: string;
  businessQuestionCode: string | null;
  businessQuestionText: string | null;
  cqCode: string;
  questionText: string;
  confidenceScore: number | null;
  status: string;
  source: string | null;
  explanation: string | null;
  evidenceSource: string | null;
  metadata: string | null;
  createdAt: string;
  updatedAt: string | null;
  reviewedAt: string | null;
}

// Flat row shape mirrored directly from CompetencyQuestionApiItem; nullable fields stay nullable
// so the UI can render "Not recorded yet" instead of inventing placeholder values.
export interface CompetencyQuestionRow {
  id: string;
  cqId: string;
  initiativeId: string;
  businessQuestionId: string;
  businessQuestionCode: string | null;
  businessQuestionText: string | null;
  question: string;
  confidence: number;
  confidenceScore: number | null;
  status: CqStatus;
  source: string | null;
  explanation: string | null;
  evidenceSource: string | null;
  metadata: string | null;
  createdAt: string;
  updatedAt: string | null;
  reviewedAt: string | null;
}

export interface CqAlert {
  id: string;
  tone: "warning" | "info" | "danger";
  text: string;
}

export const ONTOLOGY_WORKBENCH_STEPS: OntologyWorkbenchStep[] = [
  { id: 1, label: "Capture & Classify" },
  { id: 2, label: "Competency Questions & CQ Review" },
  { id: 3, label: "Entities & Relationships" },
  { id: 4, label: "Reuse & Harmonisation" },
  { id: 5, label: "Build Ontology" },
  { id: 6, label: "Ontology Library" }
];

export const FLOW_EXPLAINER_STEPS: FlowExplainerStep[] = [
  {
    id: "business-questions",
    title: "Business Questions",
    description:
      "Capture the business problems and decisions the ontology must support."
  },
  {
    id: "competency-questions",
    title: "Competency Questions",
    description: "Generate formal questions that drive ontology requirements."
  },
  {
    id: "cq-review",
    title: "CQ Review",
    description:
      "Optional gate for approving, modifying, rejecting, or skipping questions before downstream generation."
  },
  {
    id: "entities-relationships",
    title: "Entities & Relationships",
    description: "Derive concepts, attributes, relationships, and constraints."
  },
  {
    id: "reuse-harmonisation",
    title: "Reuse & Harmonisation",
    description:
      "Evaluate reuse opportunities before creating net-new ontology components."
  }
];

export interface GeneratedCqKpi {
  id: string;
  label: string;
  value: number;
}

// Dummy payload written to competency_questions.generated_cq_kpis when "Generate CQs & Concepts" is clicked.
export const DUMMY_GENERATED_CQ_KPIS: GeneratedCqKpi[] = [
  { id: "total-generated", label: "Total Generated", value: 12 },
  { id: "approved", label: "Approved", value: 7 },
  { id: "needs-review", label: "Needs Review", value: 3 },
  { id: "draft", label: "Draft", value: 2 }
];

export const CQ_ALERTS: CqAlert[] = [
  {
    id: "duplicates",
    tone: "warning",
    text: "Potential duplicate questions detected"
  },
  {
    id: "ambiguous",
    tone: "info",
    text: "Broad or ambiguous questions identified"
  },
  { id: "coverage", tone: "danger", text: "Coverage gaps identified" }
];
